import React, { useEffect, useState } from 'react';
import { Box, Paper, Tooltip } from '@mui/material';
import { TOOLTIP_PLACEMENT } from './TableSpotlight';

interface CustomElementSpotlightProps {
    isDarkMode: boolean;
    elementSelector: string;
    tooltip: React.ReactNode;
    tooltipPlacement?: TOOLTIP_PLACEMENT;
    clickable?: boolean;
    onButtonClick?: () => void;
}

const CustomElementSpotlight: React.FC<CustomElementSpotlightProps> = ({
    isDarkMode = false,
    elementSelector,
    tooltip,
    tooltipPlacement = "right" as TOOLTIP_PLACEMENT,
    clickable = false,
    onButtonClick
}) => {
    const [maskRect, setMaskRect] = useState<{
        top: number;
        left: number;
        width: number;
        height: number;
    } | null>(null);

    useEffect(() => {
        const updateRect = () => {
            const element = document.querySelector(elementSelector) as HTMLElement;
            if (element) {
                const rect = element.getBoundingClientRect();
                const top = Math.min(rect.top, rect.top) + window.scrollY;
                const left = Math.min(rect.left, rect.left) + window.scrollX;
                const bottom = Math.max(rect.bottom, rect.bottom) + window.scrollY;
                const right = Math.max(rect.right, rect.right) + window.scrollX;

                const newRect = {
                    top: top - 8,
                    left: left - 8,
                    width: right - left + 16,
                    height: bottom - top + 16,
                };

                setMaskRect(prev => {
                    if (!prev ||
                        prev.top !== newRect.top ||
                        prev.left !== newRect.left ||
                        prev.width !== newRect.width ||
                        prev.height !== newRect.height) {
                        return newRect;
                    }
                    return prev;
                });
            }
        };

        // Initial update
        updateRect();

        // Setup observers
        const setupObservers = () => {
            const element = document.querySelector(elementSelector) as HTMLElement;
            if (element) {
                const resizeObserver = new ResizeObserver(updateRect);
                resizeObserver.observe(element);
                resizeObserver.observe(document.body);

                const mutationObserver = new MutationObserver(updateRect);
                mutationObserver.observe(document.body, {
                    attributes: true,
                    childList: true,
                    subtree: true,
                    attributeFilter: ['style', 'class']
                });

                return () => {
                    resizeObserver.disconnect();
                    mutationObserver.disconnect();
                };
            }
        };

        const cleanup = setupObservers();

        // Handle window resize and scroll
        const handleResize = () => {
            updateRect();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize);
        window.addEventListener('orientationchange', handleResize);

        return () => {
            cleanup?.();
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize);
            window.removeEventListener('orientationchange', handleResize);
        };
    }, [elementSelector]);

    if (!maskRect) return null;

    const overlayStyle = {
        position: "fixed" as const,
        backgroundColor: "transparent",
        zIndex: 9999,
        pointerEvents: "none" as const,
        // backdropFilter: "blur(1px)"
    };

    if (clickable) {
        delete overlayStyle.pointerEvents;
    }

    const handleClick = () => {
        if (clickable) {
            onButtonClick?.();
        }
    };

    return (
        <>
            {/* Dark overlays */}
            <div style={{ ...overlayStyle, top: 0, left: 0, width: "100vw", height: maskRect.top, backdropFilter: "blur(1px)" }} onClick={handleClick} />
            <div style={{ ...overlayStyle, top: maskRect.top + maskRect.height, left: 0, width: "100vw", height: "100vh", backdropFilter: "blur(1px)" }} onClick={handleClick} />
            <div style={{ ...overlayStyle, top: maskRect.top, left: 0, width: maskRect.left, height: maskRect.height, backdropFilter: "blur(1px)" }} onClick={handleClick} />
            <div style={{ ...overlayStyle, top: maskRect.top, left: maskRect.left + maskRect.width, right: 0, height: maskRect.height, zIndex: 9998, backdropFilter: "blur(1px)" }} onClick={handleClick} />

            {/* Spotlight with rounded border */}
            <div
                style={{
                    ...overlayStyle,
                    position: "absolute",
                    top: maskRect.top,
                    left: maskRect.left,
                    width: maskRect.width,
                    height: maskRect.height,
                    borderRadius: "12px",
                    boxShadow: "0 0 0 9999px rgba(0,0,0,0.7)",
                    pointerEvents: "none",
                    zIndex: 9999,
                }}
                onClick={handleClick}
            />

            {/* Tooltip content */}
            <Tooltip
                open
                placement={tooltipPlacement}
                title={
                    <Paper sx={{ borderRadius: "12px", }} onClick={handleClick}>
                        {tooltip}
                    </Paper>
                }
                arrow
                componentsProps={{
                    tooltip: {
                        sx: {
                            bgcolor: "#FFF",
                            zIndex: 10000,
                            borderRadius: "12px",
                            padding: 0,
                            '& .MuiTooltip-arrow': {
                                color: '#FFF',
                            },
                            maxWidth: "350px",
                        },
                    },
                    arrow: {
                        sx: {
                            color: "#fff",
                            zIndex: 10000,
                            ...(isDarkMode ? {
                                "&:before": {
                                    backgroundColor: "#383838",
                                },
                            } : {})
                        },
                    },
                }}
                PopperProps={{
                    anchorEl: document.querySelector(elementSelector),
                    disablePortal: true,
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: tooltipPlacement.includes('right') ? [10, 0] :
                                    tooltipPlacement.includes('left') ? [-10, 0] :
                                        tooltipPlacement.includes('bottom') ? [0, 10] : [0, -10],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                altAxis: true,
                                altBoundary: true,
                                tether: true,
                                rootBoundary: 'document',
                                padding: 8,
                            },
                        },
                        {
                            name: 'zIndexFix',
                            enabled: true,
                            phase: 'beforeWrite',
                            fn: ({ state }) => {
                                state.styles.popper.zIndex = "10000";
                            },
                        },
                    ],
                }}
            >
                <span style={{ position: "absolute", top: -9999 }} />
            </Tooltip>
        </>
    );
};

export default CustomElementSpotlight; 